import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { CalendarIcon, LinkedinIcon, MailIcon, MapPinIcon, XIcon } from "lucide-react";
import type { Mentor } from "../../types/hackathon";
import { categoryLabel } from "./mentorCategories";
import { initials } from "../../utils/progress";
import { useScrollLock } from "../../hooks/useScrollLock";
interface MentorDialogProps {
  mentor: Mentor;
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function MentorDialog({
  mentor,
  onClose
}: MentorDialogProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const {
    lead,
    rest
  } = splitBio(mentor.bio);
  const hasDetails = mentor.askAbout && mentor.askAbout.length > 0 || mentor.availability;
  useScrollLock();
  useEffect(() => {
    closeRef.current?.focus({
      preventScroll: true
    });
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 p-0 sm:items-center sm:p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease
  }} onClick={onClose} data-id="element-501" data-mp="24q617nlo1154">
      <motion.div role="dialog" aria-modal="true" aria-labelledby="mentor-dialog-name" className="relative max-h-[90svh] w-full max-w-2xl overflow-y-auto rounded-t-[28px] bg-cream text-ink sm:rounded-[28px]" initial={{
      opacity: 0,
      y: 24,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 24,
      scale: 0.98
    }} transition={{
      duration: 0.25,
      ease
    }} onClick={e => e.stopPropagation()} data-id="element-502" data-mp="1q0roq6msm3li">
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-ink bg-cream transition-colors duration-150 hover:bg-ink hover:text-sun" data-id="element-503" data-mp="12b5tfu9jk8is">
          <XIcon className="h-5 w-5" data-id="element-504" data-mp="14m6l0741mgg2" />
        </button>

        {/* Who they are */}
        <header className="flex items-center gap-5 border-b border-ink/15 p-7 pr-20 md:gap-6 md:p-10 md:pr-24" data-id="element-505" data-mp="pqqqy23gqkjo">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-tomato md:h-28 md:w-28" data-id="element-506" data-mp="us683j2is6ws">
            {mentor.photo ? <img src={mentor.photo} alt="" className="h-full w-full object-cover" style={{
            objectPosition: mentor.photoPosition ?? "50% 30%"
          }} data-id="element-507" data-mp="lpx3f9txn7rb" /> : <span className="flex h-full w-full items-center justify-center font-display text-4xl uppercase" data-id="element-508" data-mp="c03ted9fv0p6">
                {initials(mentor.name)}
              </span>}
          </div>
          <div className="min-w-0" data-id="element-509" data-mp="20mmxtmd4is5">
            <p className="text-[12px] font-bold uppercase tracking-wide text-ink/60" data-id="element-510" data-mp="3fihylv18qxio">{categoryLabel(mentor.category)}</p>
            <h3 id="mentor-dialog-name" className="mt-1 font-display text-4xl uppercase leading-[0.95] md:text-5xl" data-id="element-511" data-mp="3ak6rvt68t34o">
              {mentor.name}
            </h3>
            <p className="mt-2 text-[15px] font-medium leading-snug" data-id="element-512" data-mp="3ogtqs1hra3d8">{mentor.title || mentor.organization}</p>
            {mentor.location && <p className="mt-1.5 flex items-center gap-1.5 text-[14px] text-ink/70" data-id="element-513" data-mp="2ay5t4oewo7k">
                <MapPinIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-514" data-mp="3tj2lp3e6vfhz" />
                {mentor.location}
              </p>}
          </div>
        </header>

        <div className="space-y-8 p-7 md:p-10" data-id="element-515" data-mp="72ok1lqgzw49">
          {mentor.note && <p className="rounded-xl bg-tomato/15 px-4 py-3 text-sm font-semibold text-[#8f2e1a]" data-id="element-516" data-mp="74sy2wi32bi9">{mentor.note}</p>}

          {/* Background */}
          {lead && <section aria-labelledby="mentor-about" data-id="element-517" data-mp="g4xwwi97o1kt">
              <h4 id="mentor-about" className="text-[12px] font-bold uppercase tracking-wide text-ink/60" data-id="element-518" data-mp="1060tbo7zhnku">
                About
              </h4>
              <p className="mt-3 text-[18px] font-semibold leading-snug" data-id="element-519" data-mp="145jac8wopeie">{lead}</p>
              {rest.length > 0 && <ul className="mt-4 space-y-2.5" data-id="element-520" data-mp="1905ebr0fs4sg">
                  {rest.map(s => <li key={s} className="flex gap-3 text-[15px] leading-relaxed text-ink/80" data-id="element-521" data-mp="1dz06vko7h7pr">
                      <span className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-ink/50" aria-hidden="true" data-id="element-522" data-mp="h8puu5u6ey6g" />
                      <span data-id="element-523" data-mp="la1m0fsp7rgr">{s}</span>
                    </li>)}
                </ul>}
            </section>}

          {/* What to talk about, and when */}
          {hasDetails && <div className="grid gap-8 border-t border-ink/15 pt-8 sm:grid-cols-2" data-id="element-524" data-mp="21mzyemtcd25e">
              {mentor.askAbout && mentor.askAbout.length > 0 && <section aria-labelledby="mentor-ask" data-id="element-525" data-mp="26jqevb925zrc">
                  <h4 id="mentor-ask" className="text-[12px] font-bold uppercase tracking-wide text-ink/60" data-id="element-526" data-mp="3r3vikh1wxfwl">
                    Ask me about
                  </h4>
                  <ul className="mt-3 space-y-2" data-id="element-527" data-mp="3hdspyhype3ys">
                    {mentor.askAbout.map(t => <li key={t} className="border-l-2 border-ink pl-3 text-[15px] font-semibold leading-snug" data-id="element-528" data-mp="3cepikqv93lnn">
                        {t}
                      </li>)}
                  </ul>
                </section>}
              {mentor.availability && <section aria-labelledby="mentor-when" data-id="element-529" data-mp="2yeyat7vbv1i3">
                  <h4 id="mentor-when" className="text-[12px] font-bold uppercase tracking-wide text-ink/60" data-id="element-530" data-mp="2yfpy7vertsxa">
                    When &amp; where
                  </h4>
                  <p className="mt-3 text-[15px] leading-relaxed" data-id="element-531" data-mp="wo395zefw83g">{mentor.availability}</p>
                </section>}
            </div>}

          {/* Get in touch */}
          {(mentor.email || mentor.bookingUrl || mentor.linkedin) && <section aria-labelledby="mentor-contact" className="border-t border-ink/15 pt-8" data-id="element-532" data-mp="2m2vuql7slkeb">
              <h4 id="mentor-contact" className="text-[12px] font-bold uppercase tracking-wide text-ink/60" data-id="element-533" data-mp="2qx8ih7gaxhvv">
                Get in touch
              </h4>
              <div className="mt-3 flex flex-wrap gap-3" data-id="element-534" data-mp="2h6nj3hst09bw">
                {mentor.email && <a href={`mailto:${mentor.email}`} className="inline-flex min-w-0 items-center gap-2 rounded-full bg-ink px-5 py-3 text-[13px] font-semibold text-sun transition-colors duration-150 hover:bg-ink/85" data-id="element-535" data-mp="34w87ig7q7ysk">
                    <MailIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-536" data-mp="2zvkg3tfl5fxt" />
                    <span className="truncate" data-id="element-537" data-mp="1kfd1b80fvaut">{mentor.email}</span>
                  </a>}
                {mentor.bookingUrl && <a href={mentor.bookingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border-2 border-ink px-5 py-2.5 text-[13px] font-semibold uppercase tracking-wide transition-colors duration-150 hover:bg-ink hover:text-sun" data-id="element-538" data-mp="1ohq2rk85kmls">
                    <CalendarIcon className="h-4 w-4" aria-hidden="true" data-id="element-539" data-mp="1lxtspb7pfng7" />
                    Book office hours
                  </a>}
                {mentor.linkedin && <a href={mentor.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border-2 border-ink px-5 py-2.5 text-[13px] font-semibold uppercase tracking-wide transition-colors duration-150 hover:bg-ink hover:text-sun" data-id="element-540" data-mp="t8wb7abbhuoi">
                    <LinkedinIcon className="h-4 w-4" aria-hidden="true" data-id="element-541" data-mp="ai54kgipsidt" />
                    LinkedIn
                  </a>}
              </div>
              {mentor.email && <p className="mt-3 text-[13px] text-ink/60" data-id="element-542" data-mp="3q29ecniapazt">Put your team name in the email subject line.</p>}
            </section>}
        </div>
      </motion.div>
    </motion.div>;
}

/** First sentence becomes a short lead; the rest become scannable points. */
function splitBio(bio: string): {
  lead: string;
  rest: string[];
} {
  const sentences = (bio.match(/[^.!?]+(?:[.!?]+|$)/g) ?? []).map(s => s.trim())
  // re-join fragments split on abbreviations like "Inc." or "St."
  .reduce<string[]>((acc, s) => {
    const prev = acc[acc.length - 1];
    if (prev && (/\b[A-Z][a-z]{0,2}\.$/.test(prev) || /^[a-z]/.test(s))) acc[acc.length - 1] = `${prev} ${s}`;else if (s) acc.push(s);
    return acc;
  }, []);
  const [lead = "", ...rest] = sentences;
  return {
    lead,
    rest
  };
}